import { ellipsesInMiddle } from "@me8eon/string_utils";
import { AiToolImpl } from "@me8eon/aiclient";
import { ElasticSearchConfig, ElasticSearchKnnSearchResponse } from "@me8eon/ai.tools.utils";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

type ToolArgs = {
    query: string;
    deviceName?: string;
    homepageUrl?: string;
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (esConfig: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

type ElasticsearchIntuneDocument = {
    _index: string;
    _id: string;
    _score: number;
    _source: {
        title: string;
        description?: string;
        keywords?: string;
        category: string;
        response?: string;
    };
};

const mapElasticSearchDocumentsToString = (documents: ElasticsearchIntuneDocument[]): string => {
    return documents.map((doc, index) => {
        const descriptionText = doc._source.description || doc._source.keywords || "";
        const docIdWithIndex = `${doc._index}/${doc._id}`;
        return `**Action ${index + 1}**:\n            - Title: ${doc._source.title}\n            - Description: ${ellipsesInMiddle(descriptionText, 200)}\n            - Category: ${doc._source.category}\n            - Elasticsearch Document ID: ${docIdWithIndex}\n            - Index: ${doc._index}`;
    }).join("\n");
}

export const FetchIntuneActionsToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "intune",
        toolDefn: {
            type: "function",
            name: "fetch_intune_actions",
            description: "This tool fetches Intune remediation actions from Elasticsearch using semantic search. Pass the user's exact query.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The user's exact original query.",
                    },
                    deviceName: {
                        type: "string",
                        description: "Optional: device name for follow-up action calls.",
                    },
                    homepageUrl: {
                        type: "string",
                        description: "Optional: homepage URL for follow-up action calls.",
                    },
                }
            },
        },
        execute: async (indices, { query }) => {
            const {
                axios,
                blacklistedFields = ["full_text_embeddings", "vectorisation_model"],
            } = esConfig;

            if (!query || query.trim().length === 0) {
                return {
                    value: "Please provide a query to search Intune actions.",
                    _meta: {
                        selectedIndex: "none",
                        skipIntune: true,
                    }
                };
            }

            if (!indices || indices.length === 0) {
                indices = ["intune-prod"];
            }

            const top = 3;
            const indiciesAsString = indices.join(",");
            const url = `${EnvironmentConfig.elasticSearchProxyUrl}/${indiciesAsString}/_search`;
            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${await jwtTokenFn()}`,
            };

            const body = {
                _source: {
                    excludes: blacklistedFields,
                },
                knn: {
                    field: "full_text_embeddings",
                    query_vector_builder: {
                        text_embedding: {
                            model_id: ".multilingual-e5-small_linux-x86_64",
                            model_text: query,
                        },
                    },
                    k: top,
                    num_candidates: top + 10,
                },
            };

            const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, { headers: headers });
            const allDocuments = response.data?.hits?.hits as unknown as ElasticsearchIntuneDocument[];

            if (!allDocuments || allDocuments.length === 0) {
                return {
                    value: "No Intune actions available for this query. Use knowledge search as fallback.",
                    _meta: {
                        selectedIndex: "none",
                        skipIntune: true,
                    }
                };
            }

            const selectedDocument = allDocuments[0];
            const selectedIndex = selectedDocument._index;
            const documentId = `${selectedIndex}/${selectedDocument._id}`;
            const content = `Here are the top Intune actions found:\n\n${mapElasticSearchDocumentsToString([selectedDocument])}\n\n**ACTION_ID: ${documentId}**`;

            return {
                value: content,
                _meta: {
                    selectedIndex,
                    documentId,
                }
            };
        }
    };
};
